/**
 * Reglas de presentación del monitoreo en vivo.
 *
 * Portado del panel anterior (`src/monitoring/index.js` y
 * `src/monitoring/tanks.js`): el estado de cada lado decide su color y, cuando
 * la manguera está levantada, un lado «en línea» se muestra como
 * «despachando» (amarillo).
 *
 * Los umbrales de discrepancia de la conciliación (1 % y 3 %) son los mismos
 * del panel anterior.
 */

import type { BadgeVariant } from '@/components/ui/badge';
import { ApiError } from '@/lib/http/api-error';

import type {
  DatosLado,
  DatosTanqueControlador,
  LadoCatalogo,
  NozzleCatalogo,
  PaqueteLado,
  TanqueCatalogo,
} from '../monitoreo.types';

export type TonoLado = 'offline' | 'en-linea' | 'despachando' | 'esperando' | 'manguera-arriba';

interface EstiloLado {
  tono: TonoLado;
  etiqueta: string;
  variante: BadgeVariant;
  /** Clases del encabezado de la tarjeta del lado. */
  cabecera: string;
  /** Clases del punto de estado. */
  punto: string;
}

const ESTILOS: Record<TonoLado, EstiloLado> = {
  offline: {
    tono: 'offline',
    etiqueta: 'Fuera de servicio',
    variante: 'danger',
    cabecera: 'border-rose-200 bg-rose-50',
    punto: 'bg-rose-500',
  },
  'en-linea': {
    tono: 'en-linea',
    etiqueta: 'En línea',
    variante: 'success',
    cabecera: 'border-emerald-200 bg-emerald-50',
    punto: 'bg-emerald-500',
  },
  despachando: {
    tono: 'despachando',
    etiqueta: 'Despachando',
    variante: 'warning',
    cabecera: 'border-amber-200 bg-amber-50',
    punto: 'bg-amber-500',
  },
  esperando: {
    tono: 'esperando',
    etiqueta: 'Esperando cliente',
    variante: 'warning',
    cabecera: 'border-amber-200 bg-amber-50',
    punto: 'bg-amber-500',
  },
  'manguera-arriba': {
    tono: 'manguera-arriba',
    etiqueta: 'Manguera levantada',
    variante: 'warning',
    cabecera: 'border-amber-200 bg-amber-50',
    punto: 'bg-amber-500',
  },
};

/** Datos del paquete del controlador, siempre como objeto (evita `undefined`). */
export function datosLado(paquete: PaqueteLado): DatosLado {
  return paquete.Data ?? ({ Pump: paquete.Id } as DatosLado);
}

/**
 * Estilo del lado según el estado que reporta el controlador.
 *
 * Un lado «en línea» con la manguera levantada se muestra como «manguera
 * levantada»: es lo que el panel anterior pintaba de amarillo.
 */
export function estiloLado(paquete: PaqueteLado): EstiloLado {
  switch (paquete.Type) {
    case 'PumpIdleStatus':
      return datosLado(paquete).NozzleUp === 1
        ? ESTILOS['manguera-arriba']
        : ESTILOS['en-linea'];
    case 'PumpFillingStatus':
      return ESTILOS.despachando;
    case 'PumpEndOfTransactionStatus':
      return ESTILOS.esperando;
    default:
      return ESTILOS.offline;
  }
}

/** `true` cuando el lado está en servicio (cualquier estado menos offline). */
export function ladoEnServicio(paquete: PaqueteLado): boolean {
  return paquete.Type !== 'PumpOfflineStatus';
}

/** `true` cuando hay al menos un lado en servicio (habilita la parada). */
export function hayLadosEnServicio(paquetes: PaqueteLado[]): boolean {
  return paquetes.some(ladoEnServicio);
}

/**
 * Producto configurado en una manguera del lado.
 *
 * Es el equivalente a `getProduct(Pump, Nozzle)` del panel anterior, que leía
 * el arreglo de `pts_nozzles` incrustado en la página.
 */
export function mangueraDeLado(
  lado: LadoCatalogo | undefined,
  nozzle: number | undefined,
): NozzleCatalogo | null {
  if (!lado || !nozzle) return null;

  return lado.nozzles.find((manguera) => manguera.nozzle === Number(nozzle)) ?? null;
}

/** Nombre del producto de una manguera, o `null` si no está configurada. */
export function productoDeManguera(
  lado: LadoCatalogo | undefined,
  nozzle: number | undefined,
): string | null {
  return mangueraDeLado(lado, nozzle)?.producto ?? null;
}

/** `true` cuando el producto de la manguera coincide con el último despacho. */
export function esUltimoCombustible(producto: string | null, ultimo: string | undefined): boolean {
  if (!producto || !ultimo) return false;

  const a = producto.trim().toLowerCase();
  const b = ultimo.trim().toLowerCase();

  return a === b || a.includes(b) || b.includes(a);
}

/** Código de negocio devuelto por la API, si lo hay. */
export function codigoControlador(error: unknown): string | null {
  return error instanceof ApiError ? error.code : null;
}

/** `true` cuando el controlador no responde (equipo apagado o red caída). */
export function esControladorSinConexion(error: unknown): boolean {
  return codigoControlador(error) === 'PTS_SIN_CONEXION';
}

/**
 * Mensaje para el usuario cuando la pantalla no puede traer la telemetría.
 *
 * Se distinguen los avisos de configuración (telemetría desactivada, sin
 * tanques) del simple «no responde».
 */
export function mensajeControlador(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.code === 'PTS_SIN_CONEXION') {
      return (
        'El controlador no responde. Verifica que esté encendido y conectado a la red, ' +
        'e inténtalo nuevamente.'
      );
    }

    if (error.code === 'PTS_TELEMETRIA_OFF') {
      return 'La telemetría de tanques está desactivada en la configuración del sistema.';
    }

    if (error.code === 'PTS_SIN_TANQUES') {
      return 'No hay tanques configurados en el sistema.';
    }

    if (error.code === 'PTS_ERROR') {
      return error.message;
    }

    return error.message || 'No se pudo obtener la información del controlador.';
  }

  return 'No se pudo obtener la información del controlador.';
}

export type TonoDiscrepancia = 'optimo' | 'revisar' | 'alerta';

/** Tono del porcentaje de discrepancia (1 % y 3 %, como el panel anterior). */
export function tonoDiscrepancia(porcentaje: number): TonoDiscrepancia {
  if (porcentaje <= 1) return 'optimo';
  if (porcentaje <= 3) return 'revisar';

  return 'alerta';
}

/** Clases de color para el porcentaje de discrepancia. */
export const COLOR_DISCREPANCIA: Record<TonoDiscrepancia, string> = {
  optimo: 'text-emerald-700',
  revisar: 'text-amber-700',
  alerta: 'text-rose-700',
};

/** Catálogo del tanque a partir del número de sonda que reporta el controlador. */
export function tanqueDeSonda(
  tanques: TanqueCatalogo[],
  sonda: number | undefined,
): TanqueCatalogo | null {
  if (!sonda) return null;

  return tanques.find((tanque) => tanque.id_tank === Number(sonda)) ?? null;
}

/** `true` cuando la sonda está desconectada (el controlador reporta `Offline`). */
export function tanqueOffline(datos: DatosTanqueControlador | undefined): boolean {
  return (datos?.Status ?? '') === 'Offline';
}

/**
 * Porcentaje de llenado del tanque.
 *
 * El controlador lo envía en `TankFillingPercentage`; si no viene, se calcula
 * con la capacidad configurada del tanque.
 */
export function porcentajeLlenado(
  datos: DatosTanqueControlador | undefined,
  capacidad = 0,
): number {
  if (!datos || tanqueOffline(datos)) return 0;

  if (typeof datos.TankFillingPercentage === 'number') return datos.TankFillingPercentage;

  if (capacidad > 0 && typeof datos.ProductVolume === 'number') {
    return Math.min(Math.max((datos.ProductVolume / capacidad) * 100, 0), 100);
  }

  return 0;
}

/**
 * Altura de cada capa del tanque (producto y agua) para el gráfico de barras.
 *
 * El agua se acota a 5 % de la altura, como en el panel anterior, para que la
 * capa no se coma visualmente al producto.
 */
export function capasTanque(
  datos: DatosTanqueControlador | undefined,
  capacidad = 0,
): { llenado: number; agua: number; producto: number } {
  const llenado = porcentajeLlenado(datos, capacidad);
  const alturaProducto = Number(datos?.ProductHeight ?? 0);
  const alturaAgua = Number(datos?.WaterHeight ?? 0);

  const agua =
    alturaAgua > 0 && alturaProducto > 0
      ? Math.min((alturaAgua / alturaProducto) * llenado, 5)
      : 0;

  return { llenado, agua, producto: Math.max(llenado - agua, 0) };
}

/** Color del producto para la interfaz (usa el configurado en la base de datos). */
export function colorProducto(color: string | null | undefined, respaldo = '#64748b'): string {
  return color && color.trim() !== '' ? color : respaldo;
}

/**
 * Volumen en galones con 3 decimales.
 *
 * El panel anterior mostraba siempre tres decimales (`toFixed(3)`) porque los
 * despachos pequeños (calibraciones, pruebas) se pierden con dos.
 */
export function galones(valor: number | null | undefined): string {
  return Number(valor ?? 0).toLocaleString('es-PE', {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  });
}

/** Medida lineal o de temperatura con 2 decimales como máximo. */
export function medida(valor: number | null | undefined): string {
  return Number(valor ?? 0).toLocaleString('es-PE', { maximumFractionDigits: 2 });
}
